//
// Liraglutide (Saxenda®/Biolide®) PGD — AHT weight management
// Source: AHT_Liraglutide_Clinical_Pathway.docx v1.0
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis — from source document:
//   Initial (weeks 1–2): 1 pen (F2F required)
//   Weeks 3–4: up to 2 pens
//   Maintenance (3.0mg, week 5+): up to 5 pens per episode
//   Max 3 maintenance episodes before mandatory 12-week efficacy review
//
// BIOLOGICAL PRODUCT — record BRAND NAME (Saxenda® or Biolide®)
// AND BATCH NUMBER at every supply.

import type { PGD } from "../../types";

export const pgdAhtWtLiraglutide: PGD = {
  id: "pgd-aht-wt-liraglutide",
  reference: "AHT-WT-LIRA",
  version: "1.0",
  drugRef: "liraglutide",
  indication: "Weight management — liraglutide (Saxenda®/Biolide®)",
  validFrom: "",         // TODO: insert valid from date when signed
  expiryDate: "",        // TODO: insert expiry date when signed
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 12, max: 74 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
    notes: "Adults ≥18: standard eligibility. Adolescents 12–17: additional pre-supply checks required (weight >60kg, IOTF BMI cut-off, Gillick/capacity, no eating disorder, no safeguarding concern). Age 75+: not recommended — limited therapeutic experience.",
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "remote_permitted",
    // ALL supply to adolescents (under 18) must be face-to-face.
    // Adults: F2F for initial supply; subsequent may be remote (pharmacist
    // physically present at registered premises, GPhC February 2025).
  },

  duration: {
    days: 28,
    comment: "28-day supply. Weeks 1–2: 1 pen. Weeks 3–4: up to 2 pens. Maintenance (3.0mg): up to 5 pens per episode.",
  },

  doseBands: [
    {
      id: "lira-step1",
      doseStepId: "lira-0_6mg",
      resolveBy: "single",
      dose: 0.6,
      frequency: "Once daily subcutaneous injection",
      comment: "Week 1: starting dose — minimum 1 week at each step",
      formulations: [
        {
          formulationId: "liraglutide-6mg-3ml-pen",
          instruction: "Inject 0.6mg subcutaneously once daily",
          quantity: { count: 1, label: "1 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 2 },
        },
      ],
    },
    {
      id: "lira-step2",
      doseStepId: "lira-1_2mg",
      resolveBy: "single",
      dose: 1.2,
      frequency: "Once daily subcutaneous injection",
      comment: "Week 2: +0.6mg if tolerated — minimum 1 week at each step",
      formulations: [
        {
          formulationId: "liraglutide-6mg-3ml-pen",
          instruction: "Inject 1.2mg subcutaneously once daily",
          quantity: { count: 1, label: "1 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 2 },
        },
      ],
    },
    {
      id: "lira-step3",
      doseStepId: "lira-1_8mg",
      resolveBy: "single",
      dose: 1.8,
      frequency: "Once daily subcutaneous injection",
      comment: "Week 3: +0.6mg if tolerated — minimum 1 week at each step",
      formulations: [
        {
          formulationId: "liraglutide-6mg-3ml-pen",
          instruction: "Inject 1.8mg subcutaneously once daily",
          quantity: { count: 1, label: "1 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen (up to 2)" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 2 },
        },
      ],
    },
    {
      id: "lira-step4",
      doseStepId: "lira-2_4mg",
      resolveBy: "single",
      dose: 2.4,
      frequency: "Once daily subcutaneous injection",
      comment: "Week 4: +0.6mg if tolerated — minimum 1 week at each step",
      formulations: [
        {
          formulationId: "liraglutide-6mg-3ml-pen",
          instruction: "Inject 2.4mg subcutaneously once daily",
          quantity: { count: 2, label: "Up to 2 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 2 },
        },
      ],
    },
    {
      id: "lira-step5-maintenance",
      doseStepId: "lira-3_0mg",
      resolveBy: "single",
      dose: 3.0,
      frequency: "Once daily subcutaneous injection",
      comment: "Week 5+: maintenance dose — MAXIMUM. Up to 5 pens per episode. Max 3 episodes before mandatory 12-week efficacy review.",
      formulations: [
        {
          formulationId: "liraglutide-6mg-3ml-pen",
          instruction: "Inject 3.0mg subcutaneously once daily",
          quantity: { count: 5, label: "Up to 5 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen" },
          allowQuantityOverride: true,
          quantityOverrideBounds: { min: 1, max: 5 },
        },
      ],
    },
  ],

  preSupplyChecks: [
    // ADULT EXCLUSIONS — Source: Step 2 of AHT Liraglutide pathway v1.0
    {
      id: "ex-adult-bmi-below-27",
      question: "Is the adult patient's BMI below 27 kg/m²?",
      hint: "Adults (≥18 years): BMI <27 kg/m² is an exclusion criterion.",
      patientGroup: "adult",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-adult-bmi-27-30-no-comorbidity",
      question: "Is the adult patient's BMI 27–30 kg/m² without a weight-related comorbidity?",
      hint: "Eligible comorbidities for this threshold: dysglycaemia, hypertension, dyslipidaemia, or OSA only.",
      patientGroup: "adult",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity",
    },
    {
      id: "ex-adult-age-75-plus",
      question: "Is the adult patient aged 75 years or over?",
      hint: "Not recommended — limited therapeutic experience in this age group.",
      patientGroup: "adult",
      autoAnswerFrom: "age_under_75",
    },
    // ADOLESCENT EXCLUSIONS
    {
      id: "ex-adol-age-under-12",
      question: "Is the adolescent patient under 12 years of age?",
      patientGroup: "adolescent",
      autoAnswerFrom: "age_under_12",
    },
    {
      id: "ex-adol-weight-60kg-or-below",
      question: "Is the adolescent patient's body weight 60 kg or below?",
      patientGroup: "adolescent",
    },
    {
      id: "ex-adol-bmi-below-iotf",
      question: "Is the adolescent patient's BMI below the IOTF age/sex-specific obesity cut-off?",
      hint: "See Appendix A of liraglutide PGD for IOTF cut-off tables.",
      patientGroup: "adolescent",
    },
    {
      id: "ex-adol-gillick-not-established",
      question: "Has Gillick competence not been established (ages 12–15) and is parental/guardian consent unavailable?",
      patientGroup: "adolescent",
    },
    {
      id: "ex-adol-eating-disorder",
      question: "Is there a suspected or confirmed eating disorder in the adolescent patient?",
      hint: "Refer to CAMHS if suspected — do not supply.",
      patientGroup: "adolescent",
    },
    {
      id: "ex-adol-safeguarding",
      question: "Is there an active unresolved safeguarding concern for the adolescent patient?",
      hint: "Follow organisation safeguarding policy and local LSCB procedures if concern present.",
      patientGroup: "adolescent",
    },
    // ALL PATIENTS
    {
      id: "ex-hypersensitivity-liraglutide",
      question: "Does the patient have known hypersensitivity to liraglutide or any excipient?",
    },
    {
      id: "ex-nyha-iv-heart-failure",
      question: "Does the patient have congestive heart failure NYHA class IV?",
      autoAnswerFrom: "q19NyhaIV",
    },
    {
      id: "ex-endocrine-eating-disorder-obesity",
      question: "Is the patient's obesity secondary to an endocrine or eating disorder, or to medicines causing weight gain without specialist oversight?",
      autoAnswerFrom: "sharedEndocrineObesity",
    },
    {
      id: "ex-concurrent-glp1-weight-medicine",
      question: "Is the patient currently receiving another GLP-1 agonist or weight management medicine?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-t2dm-hba1c-high",
      question: "Does the patient have Type 2 diabetes with HbA1c ≥86 mmol/mol?",
      autoAnswerFrom: "t2dmHba1cHigh",
    },
    {
      id: "ex-virtual-consultation",
      question: "Is this a virtual or online consultation (for initial supply, or any supply to adolescents)?",
      hint: "F2F required for initial supply to adults and ALL supply to adolescents.",
      autoAnswerFrom: "face_to_face_confirmed",
    },
  ],

  interactions: [
    {
      id: "int-concurrent-glp1",
      interactionName: "Concurrent GLP-1 agonists",
      question: "Is the patient currently taking another GLP-1 agonist?",
      hint: "Do not use concurrently with liraglutide.",
      affectedDrugs: ["liraglutide"],
      excludeIfUnmanageable: true,
      managementPlanRequired: false,
    },
    {
      id: "int-insulin-sulphonylureas",
      interactionName: "Insulin or sulphonylureas",
      question: "Is the patient taking insulin or a sulphonylurea?",
      hint: "Increased hypoglycaemia risk in T2DM — review and consider dose reduction before initiation.",
      affectedDrugs: ["liraglutide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-warfarin-coumarin",
      interactionName: "Warfarin or coumarin anticoagulants",
      question: "Is the patient taking warfarin or a coumarin anticoagulant?",
      hint: "Delayed gastric emptying may affect absorption — monitor INR more frequently after initiation.",
      affectedDrugs: ["liraglutide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-narrow-therapeutic-index",
      interactionName: "Narrow therapeutic index oral medicines",
      question: "Is the patient taking any narrow therapeutic index oral medicines?",
      hint: "Delayed gastric emptying may affect absorption — monitor where clinically relevant.",
      affectedDrugs: ["liraglutide"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-other-weight-medicines",
      interactionName: "Other weight management medicines",
      question: "Is the patient taking any other weight management medicines?",
      hint: "Do not use concurrently.",
      affectedDrugs: ["liraglutide"],
      excludeIfUnmanageable: true,
      managementPlanRequired: false,
    },
  ],

  cautions: [
    {
      id: "caut-pancreatitis",
      text: "Pancreatitis: advise patients to report persistent severe abdominal pain; discontinue if suspected; do not restart if confirmed",
    },
    {
      id: "caut-heart-rate",
      text: "Heart rate: mean increase ~2.5 bpm; monitor resting HR; discontinue if clinically significant sustained increase",
    },
    {
      id: "caut-cholelithiasis",
      text: "Cholelithiasis/cholecystitis: higher rate than placebo; advise right upper quadrant pain, nausea, fever → refer",
    },
    {
      id: "caut-dehydration-renal",
      text: "Dehydration and acute renal failure with GI effects — monitor CKD patients closely; encourage hydration",
    },
    {
      id: "caut-not-insulin-substitute",
      text: "Not a substitute for insulin — do not discontinue insulin without prescriber advice",
    },
    {
      id: "caut-dka-risk",
      text: "DKA risk in insulin-dependent patients with rapid insulin discontinuation",
    },
    {
      id: "caut-aspiration",
      text: "Aspiration risk — advise anaesthetists/surgical team of liraglutide use before GA or sedation",
      adviceText: "Patient should inform anaesthetist/surgical team before any general anaesthetic or sedation.",
    },
    {
      id: "caut-thyroid",
      text: "Thyroid disease — monitor where clinically indicated",
    },
    {
      id: "caut-injection-rotation",
      text: "Rotate injection sites every injection — risk of cutaneous amyloidosis",
    },
    {
      id: "caut-adolescent-vomiting",
      text: "Adolescents: vomiting ~2× more frequent; monitor for disordered eating and psychological distress at every review",
    },
    {
      id: "caut-mild-moderate-renal",
      text: "Mild/moderate renal impairment (CrCl ≥30): no dose adjustment; may experience more GI effects",
    },
    {
      id: "caut-mild-moderate-hepatic",
      text: "Mild/moderate hepatic impairment: use with caution; not fully investigated",
    },
    {
      id: "caut-elderly",
      text: "Elderly (65–74 years): no dose adjustment; may experience more GI effects",
    },
  ],

  counselling: [
    "Inject subcutaneously into abdomen, thigh, or upper arm — rotate sites at every injection; document rotation",
    "Before first use: store 2–8°C; keep cap on to protect from light; do not freeze",
    "After first use: store ≤30°C or refrigerated for up to 1 month; keep cap on",
    "Shelf life unopened: Saxenda® 30 months; Biolide® 24 months",
    "Check cold chain integrity; pen must not have been frozen",
    "Check solution is clear and colourless; do not supply if discoloured",
    "Record BRAND NAME AND BATCH NUMBER at every supply (biological traceability required)",
    "Supply compatible needles (not included): Saxenda® — NovoFine® or NovoTwist® ≤8mm ≥32G; Biolide® — BD Ultra-Fine™ or NovoFine® ≤8mm ≥32G",
    "Instruct on injection technique; demonstrate first dose if needed",
    "For adolescents: ensure parent/guardian also understands technique and schedule",
    "Advise patient to inform GP on initiation and at any significant clinical change",
    "Pancreatitis warning: severe abdominal pain → stop injecting, attend A&E",
    "Gallstones: right upper quadrant pain, nausea, fever → seek advice",
    "Tell anaesthetist/surgical team you are taking liraglutide before any GA or sedation",
    "Adults: 12-week review on 3.0mg — <5% weight loss leads to discontinuation",
    "Adolescents: 12-week review on max dose — <4% BMI/z-score reduction leads to discontinuation",
    "Documentation retained 8 years",
    "Adolescents additionally: document Gillick competence/capacity, consent/assent, parent/guardian involvement, BMI z-score, safeguarding considerations at every review",
  ],

  efficacyReview: {
    timepoint: "12 weeks on maximum tolerated dose",
    threshold: "Adults: ≥5% weight loss. Adolescents: ≥4% BMI or BMI z-score reduction.",
    actionIfNotMet: "Discontinue and re-evaluate — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Weight",
      "BMI",
      "Waist circumference",
      "Resting heart rate",
      "Blood pressure",
      "GI side effects",
      "Hydration",
      "HbA1c and fasting glucose if prediabetes or T2DM",
      "INR if on warfarin/coumarin anticoagulants",
      "Thyroid function if pre-existing thyroid disease or new symptoms",
      "Record brand name and batch number at every supply",
      "Adolescents additionally: BMI z-score (RCPCH charts), Gillick competence/capacity, parental involvement, signs of disordered eating or psychological distress, safeguarding considerations",
    ],
    discontinueIf: [
      "Less than 5% weight loss in adults after 12 weeks on 3.0mg/day",
      "Less than 4% BMI or BMI z-score reduction in adolescents after 12 weeks on maximum tolerated dose",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 28,
    drugSpecificReviewDays: 84,
    drugSpecificReviewLabel: "12-week efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss after 12 weeks on 3.0mg/day (adults). Discontinue if <4% BMI or BMI z-score reduction after 12 weeks on maximum tolerated dose (adolescents).",
    sectionBReviewDays: 168,
    bmiFloorKgM2: null,
    titrationSchedule: {
      weeklyUntilWeek: 4,
      weeklyIntervalDays: 7,
    },
    maxEpisodesBeforeReview: 3,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: true,
  },

  adverseEventGuidance: [
    "Anaphylaxis: STOP immediately; discontinue permanently",
    "Pancreatitis: STOP; do not restart if confirmed",
    "Clinically significant sustained HR increase: discontinue",
    "Gallstones/cholecystitis: refer",
    "Acute renal failure with GI side effects: review urgently",
    "Dehydration: assess and manage",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "No black triangle status — but all suspected ADRs should be reported",
  ],
};
